import { capitalize } from 'lodash';

import styled from '@emotion/styled';

import {
  calculateAge,
  DEFAULT_IMAGE_URL,
  formatExperienceDate,
} from '../../../utils/utils';
import EducationCard from '../../Card/EducationCard';
import ExperienceCard from '../../Card/ExperienceCard';
import LicenseCard from '../../Card/LicenseCard';
import Img from '../../Img/Img';
import {
  SegmentedViewControllerLite,
  SegmentedViewDataLite,
  SegmentedViewLite,
} from '../../SegmentedView/SegmentedViewLite';
import ModalContainer from '../ModalContainer';
import useGetApplicantData from '../../../hooks/jobs/useGetApplicantData';
import { ModalContext } from '../../../context/ModalContext';
import { useContext, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { FormComponent } from '../../Form/FormComponent';
import {
  CreateJobInterviewDataType,
  createJobInterviewInitialValues,
  CreateJobInterviewSchema,
} from '../../../models/jobs/createJobInterview.model';
import CustomCalender from '../../Form/CustomCalender/CustomCalender';
import { useApplicantsRefresh } from '../../../context/ApplicantRefreshContext';
import useCreateJobInterviewTime from '../../../hooks/jobs/useCreateJobInterviewTime';

const segmentedControlConfData = [
  { title: 'Experience' },
  { title: 'Education' },
  { title: ' Licenses & certifications' },
];

const Calender = ({ applicantionId }: { applicantionId: string }) => {
  const { triggerRefresh } = useApplicantsRefresh();

  const { loading, createJobPostInterviewTime } = useCreateJobInterviewTime();
  const handleOnSubmit = (values: CreateJobInterviewDataType) => {
    createJobPostInterviewTime(
      { ...values, duration: '30' },
      applicantionId
    ).then(() => {
      triggerRefresh();
    });
  };

  return (
    <FormComponent
      initialValues={createJobInterviewInitialValues}
      schema={CreateJobInterviewSchema}
      onSubmit={handleOnSubmit}
    >
      <CustomCalender
        id="interviewDate"
        name="interviewDate"
        isSubmitting={loading}
      />
    </FormComponent>
  );
};

const ApplicantProfileModal = ({
  applicantId,
  applicationId,
  resume,
}: {
  applicantId: string;
  applicationId: string;
  resume: string;
}) => {
  const modalContext = useContext(ModalContext);

  const [showCal, setShowCal] = useState(false);

  const { loading, applicantData, failed, getApplicantData } =
    useGetApplicantData(applicantId, modalContext?.showModal ?? false);

  const handleTryagain = () => {
    getApplicantData(applicantId);
  };

  const handleViewCV = (link: string) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  return (
    <ModalContainer>
      {loading ? (
        <>
          <LoadingOutlined />
          <p>fetching profile...</p>
        </>
      ) : failed ? (
        <>
          <p></p>
          <Try onClick={handleTryagain}>Try again</Try>
        </>
      ) : (
        <Container>
          <BioSection>
            <BioImageArea>
              <BioImageCircle>
                <Img
                  src={applicantData?.profileImage ?? DEFAULT_IMAGE_URL}
                  alt="profile image"
                />
              </BioImageCircle>
              <BioName>
                {capitalize(applicantData?.lastName ?? '')}{' '}
                {capitalize(applicantData?.firstName ?? '')}
              </BioName>
              <BioOccupation>
                {applicantData?.occupation ?? 'N/A'}
              </BioOccupation>
            </BioImageArea>
            <Bio>{applicantData?.bio ?? 'N/A'}</Bio>
          </BioSection>

          <BasicInformationSection>
            <BasicLabel>Basic information </BasicLabel>
            <AgeArea>
              <div>
                <Label>Age</Label>
                <DarkCell>
                  {calculateAge(applicantData?.birthday ?? '')}years
                </DarkCell>
              </div>
              <div>
                <Label>Years of Experience</Label>
                <DarkCell>{applicantData?.yearsOfExperience} years</DarkCell>
              </div>
              <div>
                <Label>Location</Label>
                <DarkCell>
                  {applicantData?.state}, {applicantData?.country}
                </DarkCell>
              </div>

              <MobileViewAvailability>
                <Label>Availability</Label>
                <DarkCell>Full Time</DarkCell>
              </MobileViewAvailability>
            </AgeArea>
            <Availability>
              <WebViewAvailability>
                <div>Availability</div>
                <DarkCell>Full Time</DarkCell>
              </WebViewAvailability>
              <Button onClick={() => setShowCal(!showCal)}>
                {showCal ? 'Close Calender' : ' Schedule an interview'}
              </Button>
              <Button
                className="purple"
                onClick={() => handleViewCV(resume)}
                disabled={resume === null}
              >
                {resume ? 'View CV' : 'No applicant CV'}
              </Button>
            </Availability>
            {showCal && <Calender applicantionId={applicationId}></Calender>}
          </BasicInformationSection>

          {/*  */}
          <ExperienceSection>
            <SegmentedViewLite>
              <SegmentedViewControllerLite
                segmentedViewControllerTitle={segmentedControlConfData}
                className={'sm-header'}
              />

              <SegmentedViewDataLite>
                <div>
                  <SegmentContainer className="experiences">
                    {applicantData?.experiences.map((data, index) => (
                      <ExperienceCard
                        key={index}
                        title={data.title}
                        companyName={data.companyName}
                        employmentType={data.employmentType}
                        location={data.location}
                        startDate={data.startDate}
                        endDate={data.endDate}
                      />
                    ))}
                  </SegmentContainer>
                </div>
                <div>
                  <SegmentContainer>
                    <EducationCard
                      school={
                        applicantData?.verification?.primaryDegreeName ?? 'N/A'
                      }
                      license={
                        applicantData?.verification?.primaryDegreeCertificate ??
                        'N/A'
                      }
                      forApplicant={true}
                    />
                  </SegmentContainer>
                </div>
                <div>
                  <SegmentContainer>
                    {applicantData?.verification ? (
                      <Licenses>
                        {' '}
                        <LicenseCard
                          license={
                            applicantData?.verification?.license ?? 'N/A'
                          }
                          degree={
                            applicantData?.verification?.primaryDegreeName ??
                            'N/A'
                          }
                          profession={applicantData?.occupation ?? 'N/A'}
                          licensedAt={
                            formatExperienceDate(
                              applicantData?.verification?.yearOfCurrentLicense
                            ) ?? 'N/A'
                          }
                          forApplicant={true}
                        />
                        {applicantData.experiences &&
                          applicantData.experiences
                            .filter((exp) => exp.otherQualification !== null)
                            .map((exp, index) => (
                              <LicenseCard
                                key={index}
                                degree={exp.otherQualification}
                                licensedAt={formatExperienceDate(
                                  exp.yearOfQualification
                                )}
                                license={exp.qualificationCertificate ?? ''}
                                forApplicant={true}
                              />
                            ))}
                      </Licenses>
                    ) : !applicantData?.verification &&
                      applicantData?.experiences ? (
                      <Licenses>
                        {applicantData.experiences
                          .filter((exp) => exp.otherQualification !== null)
                          .map((exp, index) => (
                            <LicenseCard
                              key={index}
                              degree={exp.otherQualification}
                              licensedAt={formatExperienceDate(
                                exp.yearOfQualification
                              )}
                              license={exp.qualificationCertificate ?? ''}
                              forApplicant={true}
                            />
                          ))}
                      </Licenses>
                    ) : (
                      'No licenses and certificates'
                    )}
                  </SegmentContainer>
                </div>
              </SegmentedViewDataLite>
            </SegmentedViewLite>
          </ExperienceSection>
        </Container>
      )}
    </ModalContainer>
  );
};

export default ApplicantProfileModal;

const Container = styled.div`
  display: grid;
  grid-template-columns: 364px 1fr;
  gap: 30px;
  /* border: 1px solid blue; */

  & > * {
    border: 1px solid ${({ theme }) => theme.palette.greyGrey4};
    box-shadow: 0px 20px 26px 0px rgba(186, 182, 182, 0.16);
    padding: 32px;
    border-radius: 6px;
  }

  @media (max-width: 884px) {
    grid-template-columns: 1fr;

    & > * {
      border: 1px solid ${({ theme }) => theme.palette.greyGrey4};
      box-shadow: 0px 20px 26px 0px rgba(186, 182, 182, 0.16);
      padding: 10px;
      border-radius: 6px;
    }
  }
`;

const ExperienceSection = styled.div`
  display: flex;
`;

const BioSection = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: column;
  grid-row: span 2;
`;

const BioImageArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: center;
  text-align: center;
  align-items: center;
  font-size: 16px;
`;

const BioImageCircle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  width: 100px;
  overflow: hidden;
  /* border: 1px solid red; */
  border-radius: 50%;
  margin-bottom: 16px;
`;

const BioName = styled.div`
  display: flex;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.blackBlackMain};
`;

const BioOccupation = styled.div`
  display: flex;
  color: ${({ theme }) => theme.palette.mainBlue};
`;

const Bio = styled.div`
  display: flex;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.blackBlack3};
  line-height: 24px;
  max-height: 320px;
  overflow: auto;
`;

const BasicInformationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: ${({ theme }) => theme.palette.greyGrey2};

  @media (max-width: 884px) {
    grid-row: 1;
    padding: 24px;
  }
`;

const BasicLabel = styled.div`
  color: ${({ theme }) => theme.palette.blackBlack3};
  font-weight: 500;
`;

const AgeArea = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 34px;

  @media (max-width: 884px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const Label = styled.div`
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const Availability = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: repeat(3, 1fr);
  gap: 34px;

  @media (max-width: 884px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const MobileViewAvailability = styled.div`
  @media (min-width: 884px) {
    display: none;
  }
`;

const WebViewAvailability = styled.div`
  @media (max-width: 884px) {
    display: none;
  }
`;

const DarkCell = styled.div`
  color: ${({ theme }) => theme.palette.greyGrey1};
  font-size: 18px;

  @media (max-width: 884px) {
    font-size: 14px;
  }
`;

const SegmentContainer = styled.div`
  display: flex;

  &.experiences {
    flex-direction: column;
    max-height: 320px;
    margin-left: -32px;
    margin-right: -32px;
    overflow-y: auto;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-height: fit-content;
    gap: 16px;

    &.experiences {
      flex-direction: column;
      margin: 0px;
    }
  }
`;

const Button = styled.button`
  display: grid;
  place-content: center;
  color: white;
  font-weight: 500;
  font-size: 14px;
  height: fit-content;
  border-radius: 4px;
  padding: 10px 20px;
  background-color: ${({ theme }) => theme.palette.mainBlue};
  cursor: pointer;
  width: 100%;
  min-width: 11.5rem;
  border: none;

  &.purple {
    background-color: ${({ theme }) => theme.palette.mainPurple};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.palette.greyGrey2};
  }
`;

const Try = styled.p`
  color: ${({ theme }) => theme.palette.stateColorRed};
  width: 8rem;
  height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  font-size: 18px;
  font-weight: 500;
`;

const Licenses = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: 2rem;
  padding-top: 1rem;
`;
